import { FC, useState } from 'react';
import { formatCurrencyCart } from '../../utils/formatters';
import { translate } from '../../i18n';
import { InstallmentOption } from '../InstallmentOption';
import { installmentOptionStyles } from '../InstallmentOption/styles';
import type { InstallmentPlan } from '../InstallmentSelector';
import { installmentPickerStyles } from './styles';

export interface InstallmentPickerProps {
  plans?: InstallmentPlan[];
  selectedId?: string;
  onSelect?: (planId: string) => void;
  testID?: string;
}

/**
 * InstallmentPicker — Dropdown colapsable de cuotas para el flujo de pago con 2 tarjetas.
 *
 * A diferencia de InstallmentSelector (fieldset siempre expandido, usado en el flujo
 * de 1 tarjeta), este componente muestra un trigger colapsado que se expande al
 * tocarlo, reutilizando InstallmentOption para cada fila de la lista.
 */
export const InstallmentPicker: FC<InstallmentPickerProps> = ({
  plans = [],
  selectedId,
  onSelect,
  testID = 'installment-picker',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedPlan = plans.find((plan) => plan.id === selectedId);

  const handleSelect = (planId: string) => {
    onSelect?.(planId);
    setIsOpen(false);
  };

  return (
    <div style={installmentPickerStyles.container} data-testid={testID}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        data-testid={`${testID}-trigger`}
        aria-label={translate('paymentsUI.installmentPicker.ariaLabel')}
        aria-expanded={isOpen}
        style={{
          ...installmentPickerStyles.trigger,
          ...(isOpen && installmentPickerStyles.triggerOpen),
          ...(selectedPlan && installmentPickerStyles.triggerHasSelection),
        }}
      >
        {selectedPlan ? (
          <span style={installmentPickerStyles.triggerSelected}>
            <span style={installmentPickerStyles.triggerSelectedLabel}>
              {selectedPlan.months > 1
                ? translate('paymentsUI.installment.pluralShort', { months: selectedPlan.months })
                : translate('paymentsUI.installment.singular', { months: selectedPlan.months })}
            </span>
            {selectedPlan.badge && (
              <span style={installmentOptionStyles.badge}>{selectedPlan.badge.text}</span>
            )}
            <span style={installmentPickerStyles.triggerSelectedTotal}>
              {formatCurrencyCart(selectedPlan.totalAmount)}
            </span>
          </span>
        ) : (
          <span style={installmentPickerStyles.triggerPlaceholder}>
            {translate('paymentsUI.installmentPicker.placeholder')}
          </span>
        )}
        <span
          style={{
            ...installmentPickerStyles.chevron,
            ...(isOpen && installmentPickerStyles.chevronOpen),
          }}
          aria-hidden="true"
        >
          ⌄
        </span>
      </button>

      {isOpen && (
        <div
          style={{
            ...installmentPickerStyles.panel,
          }}
          data-testid={`${testID}-panel`}
        >
          {plans.map((plan) => (
            <InstallmentOption
              key={plan.id}
              months={plan.months}
              monthlyAmount={plan.monthlyAmount}
              totalAmount={plan.totalAmount}
              badge={plan.badge}
              interestRate={plan.interestRate}
              isSelected={plan.id === selectedId}
              onPress={() => handleSelect(plan.id)}
              testID={`${testID}-option-${plan.id}`}
              accessibilityLabel={
                plan.months > 1
                  ? translate('paymentsUI.installment.selectAriaLabelPlural', { months: plan.months })
                  : translate('paymentsUI.installment.selectAriaLabel', { months: plan.months })
              }
              showRadio={false}
              reserveSecondRow
            />
          ))}
        </div>
      )}
    </div>
  );
};
